// Utility helpers used throughout the component
// - createElementFromHTML: safely converts an HTML string to a DOM node
// - checkAndLoadFiles: resolves/initializes any lazy assets or modules inside a given DOM subtree
// - focusFirstInteractive: moves focus to first interactive element for better accessibility
import {createElementFromHTML, checkAndLoadFiles, focusFirstInteractive} from '../../utilities/js/helper';
import './solution-finder.scss';

class SolutionFinder {
    /**
     * Instantiate a SolutionFinder controller for a given root element.
     *
     * The component renders a step-by-step decision tree (questions with options),
     * tracks user selections, updates history state for back/forward navigation,
     * and finally renders a solution teaser when a leaf is reached.
     *
     * @param {HTMLElement} $element - Root element containing the Solution Finder markup and templates.
     * @param {HTMLElement} [$modal] - Optional modal element; when provided, focus handling is scoped within it.
     */
    constructor ($element, $modal, $modalScroller, allowHistoryUpdate) {
        // Defaults
        this.defaults = {
            initAttr: 'data-solutionfinder',
            animationAttr: 'data-solutionfinder-animation',
            autoStartAttr: 'data-solutionfinder-autostart',
            jsonPathAttr: 'data-solutionfinder-path',
            templateAttr: 'data-solutionfinder-template',
            stateAttr: 'data-solutionfinder-state',
            optionsValue: 'options',
            startButtonValue: 'start',
            prevButtonValue: 'prev',
            nextButtonValue: 'next',
            indicatorValue: 'indicator',
            currentValue: 'current',
            allValue: 'all',
            resultValue: 'result',
            blockValue: 'block'
        };

        // Elements
        this.$root = $element;
        this.$modal = $modal;
        this.$modalScroller = $modalScroller;
        this.$$templates = this.$root.querySelectorAll('template');
        this.$templatesHost = this.$$templates?.[0]?.parentNode;
        this.$form = this.$root.querySelector('form');
        this.$startButton = this.$root.querySelector(`[${this.defaults.initAttr}="${this.defaults.startButtonValue}"]`);
        this.$state = this.$root.querySelector(`[${this.defaults.stateAttr}]`);
        this.$nextButton = null;
        this.$prevButton = null;
        this.$result = null;
        this.$liveRegion = this.$root.querySelector('[aria-live]');
        this.liveRegionTexts = {};

        // Properties
        this.autoStart = this.$root.hasAttribute(this.defaults.autoStartAttr);
        this.jsonPath = this.$root.getAttribute(this.defaults.jsonPathAttr);
        this.jsonData = null;
        this.userRoute = this.autoStart ? [0] : [-1];
        this.lastId = null;
        this.selectedId = null;
        this.currentStep = 0;
        this.steps = -1;
        this.visibleIndex = 0;
        this.solutionIsActive = false;
        this.state = this.$state?.getAttribute(this.defaults.stateAttr) ? JSON.parse(this.$state?.getAttribute(this.defaults.stateAttr)) : {};
        this.allowHistoryUpdate = allowHistoryUpdate;

        // Prepare live region texts used for aria-live announcements
        this.parseLiveRegionTexts();
    }

    /**
     * Bootstraps the component: initializes input modality tracking, fetches JSON
     * decision-tree data, binds events, restores history state, and sets initial visibility.
     * Side effects:
     * - Asynchronously fetches JSON
     * - Mutates DOM and internal state
     */
    initialize () {
        if (typeof window.setupInputModalityTracking === 'function') {
            window.setupInputModalityTracking();
        }
        this.fetchJsonData(() => {
            if (this.autoStart) {
                this.startAutomatically();
            }

            this.setEvents();
            this.updateHistory();
            this.updateVisibility(0);
        });
    }

    startAutomatically () {
        this.changeStep(true, true);
        this.updateHistory();
    }

    /**
     * Announces a status message to assistive technologies via the live region/state node.
     *
     * @param {('start'|'step'|'result'|string)} type - Key that maps to a message template in this.state.
     * @param {number} [step] - Current step index used for template interpolation (replaces %step%).
     */
    announce (type, step) {
        if (!this.$state) return;

        if (this.state && this.state[type]) {
            let text = this.state[type];

            if (step !== undefined && step !== null) {
                text = String(text).replace('%step%', step);
            }

            this.$state.textContent = '';
            requestAnimationFrame(() => this.$state.textContent = text);
        }
    }

    /**
     * Advances or rewinds the wizard by a single step and renders the appropriate view.
     *
     * @param {boolean} add - true to go forward, false to go back.
     * @param {boolean} update - whether to record the selected option into the user route when moving forward.
     * @param {boolean} [updateLiveRegion=true] - whether live region announcements should be triggered.
     */
    changeStep (add, update, updateLiveRegion = true) {
        if (add) {
            this.currentStep++;
        } else {
            this.currentStep--;
        }

        this.updateVisibility(this.currentStep > 0 ? -1 : 0, updateLiveRegion);
        
        if (this.currentStep > 0) {
            update ? this.userRoute[this.currentStep] = this.selectedId : null;
            this.selectedId = this.userRoute[this.currentStep];
            this.renderContent();
        }
    }

    /**
     * Updates the tracking data attributes on the next button.
     *
     * @param {string} optionName - The selected option's key, stored in data-tracking-response.
     * @param {string} parentName - The option's parent node key, stored in data-tracking-question.
     */
    setTrackingData (optionName, parentName) {
        if (this.$nextButton != null) {
            this.$nextButton.dataset.trackingResponse = optionName;
            this.$nextButton.dataset.trackingQuestion = parentName;
        }
    }

    /**
     * Updates which block is visible and manages focusability via the inert attribute.
     * Also triggers the appropriate live region announcement for the view change.
     *
     * Visible indexes mapping:
     *  0   -> start screen
     * -1   -> question screen (a step is active)
     * -2   -> solution/result screen
     *
     * @param {number} [visibleIndex=0] - Controls which screen is visible.
     * @param {boolean} [updateLiveRegion=true] - Whether to announce the change.
     */
    updateVisibility (visibleIndex = 0, updateLiveRegion = true) {
        if (this.autoStart && (visibleIndex === -1 || visibleIndex === -2)) {
            visibleIndex = visibleIndex + 1;
        }

        this.solutionIsActive = this.autoStart ? visibleIndex === -1 : visibleIndex === -2;
        this.$root.style.setProperty('--visible-index', visibleIndex);
        const $$blocks = this.$root.querySelectorAll(`[${this.defaults.initAttr}="${this.defaults.blockValue}"]`);

        $$blocks.forEach(($block, index) => {
            if (visibleIndex * -1 === index) {
                $block.removeAttribute('inert');
            } else {
                $block.setAttribute('inert', '');
            }
        });

        if (visibleIndex === 0) {
            this.announce('start');
        } else if (visibleIndex === -2 && updateLiveRegion) {
            this.announce('result');
        } else {
            this.announce('step', this.currentStep);
        }

        this.$modalScroller?.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }

    /**
     * Loads the decision tree JSON data from the path provided on the root element
     * and initializes core state (jsonData, selectedId, total steps).
     *
     * @param {Function} callback - Called after data is successfully loaded.
     */
    fetchJsonData (callback) {
        fetch(this.jsonPath)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Failed to load solution finder data: ${response.status} ${response.statusText}`);
                }
                return response.json();
            })
            .then(data => {
                this.jsonData = data;
                this.selectedId = data.id;
                this.steps = this.computeTotalSteps(this.jsonData);

                if (typeof callback === 'function') {
                    callback(this.jsonData);
                }
            })
            .catch(err => {
                // Minimal error handling: log and keep component inert
                // Optionally, could emit an event for external handling
                console.error('[SolutionFinder] Error fetching JSON data:', err);
            });
    }

    /**
     * Reads, parses and stores live region text templates from the DOM attribute
     * data-solutionfinder-liveregion. This allows localizable/customizable messages.
     * Failing to parse safely falls back to defaults.
     */
    parseLiveRegionTexts () {
        try {
            const raw = this.$liveRegion?.getAttribute('data-solutionfinder-liveregion');
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed && typeof parsed === 'object') {
                    this.liveRegionTexts = parsed;
                }
            }
        } catch (e) {
            // Keep defaults if parsing fails
            console.warn('[SolutionFinder] Failed to parse live region texts:', e);
        }
    }

    /**
     * Very small templating helper: replaces occurrences of {key} in a template with
     * the provided values. Missing values resolve to empty strings.
     *
     * @param {string} template - Text containing {placeholders}.
     * @param {Record<string, any>} [vars={}] - Map of substitutions.
     * @returns {string}
     */
    formatText (template, vars = {}) {
        if (!template || typeof template !== 'string') return '';
        return template.replace(/\{(.*?)\}/g, (_, key) => {
            const val = vars[key.trim()];
            return (val === null || val === undefined) ? '' : String(val);
        });
    }

    /**
     * Computes the total amount of steps expected in the decision tree.
     * If all paths to leaves have the same depth, returns that depth.
     * Otherwise returns -1 to indicate a variable number of steps.
     *
     * @returns {number} Steps count or -1 if variable.
     */
    computeTotalSteps () {
        const depths = [];

        const traverse = (node, depth = 0) => {
            if (!node.options || node.options.length === 0) return;

            node.options.forEach(option => {
                const nextId = option.nextId;

                if (nextId) {
                    if (this.jsonData.nodes && this.jsonData.nodes[nextId]) {
                        traverse(this.jsonData.nodes[nextId], depth + 1);
                    } else if (this.jsonData.leaves && this.jsonData.leaves[nextId]) {
                        depths.push(depth + 1);
                    }
                } else {
                    depths.push(depth + 1);
                }
            });
        };

        traverse(this.jsonData);

        const first = depths[0];
        const allEqual = depths.every(d => d === first);

        return allEqual ? first : -1;
    }

    /**
     * Renders either the question form (for root/nodes) or the final solution teaser (for leaves)
     * depending on the current selectedId.
     * Also moves focus inside the modal (if any) for better keyboard UX.
     */
    renderContent () {
        const hasNode = this.jsonData?.nodes && Object.prototype.hasOwnProperty.call(this.jsonData.nodes, this.selectedId);
        const hasLeaf = this.jsonData?.leaves && Object.prototype.hasOwnProperty.call(this.jsonData.leaves, this.selectedId);

        if (this.jsonData.id === this.selectedId || hasNode) {
            this.renderForm();
        } else if (hasLeaf) {
            this.renderSolution();
        } else {
            // Unexpected state: selectedId not found
            console.warn('[SolutionFinder] Unknown selectedId in data:', this.selectedId);
        }

        // Move focus to the first interactive element of the newly rendered form
        if (this.$modal) {
            focusFirstInteractive(this.$modal);
        }
    }

    /**
     * Renders the solution teaser for the current leaf node and dispatches a content.loaded
     * event for downstream initializations (e.g., images, sliders). Also updates visibility
     * so that only the solution block is focusable.
     */
    renderSolution () {
        if (!this.jsonData?.leaves || !this.jsonData.leaves[this.selectedId]) {
            console.warn('[SolutionFinder] Missing leaf for selectedId:', this.selectedId);
            return;
        }

        this.$result = this.$root.querySelector(`[${this.defaults.initAttr}="${this.defaults.resultValue}"]`);
        const solutionString = this.jsonData.leaves[this.selectedId].solutionTeaser;
        const created = createElementFromHTML(solutionString);

        // Ensure we always work with a single container element, even if the
        // HTML string produced multiple top-level nodes.
        let $resultEl;
        if (Array.isArray(created)) {
            $resultEl = document.createElement('div');
            created.forEach(node => {
                if (node) {
                    $resultEl.appendChild(node);
                }
            });
        } else {
            $resultEl = created;
        }

        if (!($resultEl instanceof Element)) {
            console.warn('[SolutionFinder] Result content could not be created as an element.');
            return;
        }

        $resultEl.setAttribute(this.defaults.initAttr, this.defaults.resultValue);

        if (this.$result) {
            this.$result.replaceWith($resultEl);
        } else {
            this.$root.appendChild($resultEl);
        }

        this.$result = $resultEl;
        this.updateVisibility(-2);

        checkAndLoadFiles(this.$result).then(() => {
            const contentUpdateEvent = new CustomEvent('content.loaded', {
                detail: { $context: this.$result },
                bubbles: true
            });

            window.dispatchEvent(contentUpdateEvent);
            window.checkInview();

        });
    }

    /**
     * Renders the question form for the current node by cloning <template> elements
     * inside the component. It injects the current question, the options, and the step
     * indicator (if steps are consistent).
     * After rendering, it refreshes references and re-binds dynamic event handlers.
     */
    renderForm () {
        let $formFragment = null;
        const data = this.jsonData.id === this.selectedId ? this.jsonData : this.jsonData?.nodes[this.selectedId];
        if (!data || typeof data !== 'object') return;

        let $options = null;

        const setText = ($el, value) => {
            if (!$el) return;
            $el.textContent = (value === null || value === undefined) ? '' : String(value);
        };

        const setValue = ($el, value) => {
            if (!$el) return;
            if ('value' in $el) {
                $el.value = value ?? '';
            } else {
                $el.setAttribute('value', value ?? '');
            }
        };

        const updateIndicator = ($el) => {
            if (this.steps >= 0) {
                const $current = $el?.querySelector(`[${this.defaults.initAttr}="${this.defaults.currentValue}"]`);
                const $all = $el?.querySelector(`[${this.defaults.initAttr}="${this.defaults.allValue}"]`);

                if ($current) $current.textContent = this.currentStep;
                if ($all) $all.textContent = this.steps;
            } else {
                const $indicator = $el.querySelector(`[${this.defaults.initAttr}="${this.defaults.indicatorValue}"]`);
                $indicator?.remove();
            }
        };

        this.$$templates.forEach($template => {
            const templateId = $template.getAttribute(this.defaults.templateAttr);
            if (!templateId) return;

            switch (templateId) {
                case 'form': {
                    $formFragment = $template.content.cloneNode(true);

                    // Ensure no pre-set animation attribute is present in the cloned content
                    // so we can reliably (re)trigger the animation after insertion.
                    const animAttr = this.defaults.animationAttr;
                    $formFragment.querySelectorAll(`[${animAttr}]`).forEach(el => el.removeAttribute(animAttr));

                    $options = $formFragment.querySelector(
                        `[${this.defaults.initAttr}="${this.defaults.optionsValue}"]`
                    );

                    const $question = $formFragment.querySelector(
                        `[${this.defaults.templateAttr}="question"]`
                    );

                    setText($question, data.question);
                    updateIndicator($formFragment);

                    break;
                }

                case 'options': {
                    const source = data[templateId];
                    if (!source || typeof source !== 'object') break;

                    Object.values(source).forEach(subObj => {
                        if (!subObj || typeof subObj !== 'object') return;

                        const $optionClone = $template.content.cloneNode(true);

                        Object.entries(subObj).forEach(([subKey, val]) => {
                            const $child = $optionClone.querySelector(
                                `[${this.defaults.templateAttr}="${subKey}"]`
                            );

                            if (!$child) return;

                            if (val === null) {
                                $child.remove();
                            } else if (subKey === 'nextId') {
                                setValue($child, val);
                            } else if (subKey === 'icon') {
                                $child.innerHTML = val;
                            } else {
                                setText($child, val);
                            }
                        });

                        const $radioInput = $optionClone.querySelector(
                            `[${this.defaults.templateAttr}="nextId"]`
                        );

                        if ($radioInput) {
                            $radioInput.dataset.optionName = subObj.name ?? '';
                            $radioInput.dataset.parentName = subObj.parentName ?? '';
                        }

                        $options?.appendChild($optionClone);
                    });

                    break;
                }
            }
        });

        if ($formFragment) {
            const $oldForm = this.$form;

            const insertNewForm = () => {
                if (this.$form) {
                    this.$form.replaceWith($formFragment);
                } else {
                    this.$templatesHost?.appendChild($formFragment);
                }

                this.refreshDomRefs();
                // Trigger fade-in on the newly inserted form element (not the fragment).
                const $newForm = this.$form;
                if ($newForm) {
                    // Use RAF to ensure style/state change is observed and animation starts.
                    requestAnimationFrame(() => {
                        $newForm.setAttribute(this.defaults.animationAttr, 'fade-in');
                    });
                }
            };

            if ($oldForm) {
                $oldForm.setAttribute(this.defaults.animationAttr, 'fade-out');
                $oldForm.addEventListener('animationend', () => {
                    insertNewForm();
                }, {once: true});
            } else {
                insertNewForm();
            }
        }
    }

    /**
     * Refreshes cached DOM references after (re-)rendering the form and attaches
     * the event listeners that depend on the new DOM.
     */
    refreshDomRefs () {
        this.$form = this.$root.querySelector('form');
        this.$prevButton = this.$root.querySelector(`[${this.defaults.initAttr}="${this.defaults.prevButtonValue}"]`);
        this.$nextButton = this.$root.querySelector(`[${this.defaults.initAttr}="${this.defaults.nextButtonValue}"]`);

        this.setEvents(true);
    }

    /**
     * Synchronizes browser history with the current step so back/forward navigation
     * works as expected within the modal or page.
     * Uses history.pushState with a small state payload.
     */
    updateHistory () {
        if (!this.allowHistoryUpdate) return;
        const state = history.state;
        const prevStep = state && typeof state.solutionFinder === 'number' ? state.solutionFinder : null;

        if (prevStep !== this.currentStep) {
            history.pushState({solutionFinder: this.currentStep}, '', null);
        }
    }

    /**
     * Programmatically jumps to a target step by iteratively calling changeStep
     * forward or backward. Optionally suppresses live region announcements to
     * avoid noise during fast transitions (e.g., when closing a modal).
     *
     * @param {number} targetStep - The step index to navigate to.
     * @param {boolean} [updateLiveRegion=true] - Whether to announce each step.
     */
    goToStep (targetStep, updateLiveRegion = true) {
        if (typeof targetStep !== 'number') return;
        if (targetStep === this.currentStep) return;
        const movingForward = () => targetStep > this.currentStep;

        while (this.currentStep !== targetStep) {
            this.changeStep(movingForward(), false, updateLiveRegion);
        }
    }

    /**
     * Attaches event listeners. On first call (update=false), binds root-level events
     * such as start button, history popstate, and modal open/close integration.
     * On subsequent calls (update=true), binds events that depend on rendered form DOM
     * such as option change, next, and previous buttons.
     *
     * @param {boolean} [update=false] - Whether to attach dynamic (form-level) events.
     */
    setEvents (update = false) {
        if (update) {
            this.$form?.addEventListener('change', () => {
                const $checkedOption = this.$form?.querySelector('input[type="radio"]:checked');

                if ($checkedOption) {
                    this.selectedId = Number($checkedOption.value);

                    this.setTrackingData(
                        $checkedOption.dataset.optionName,
                        $checkedOption.dataset.parentName
                    );

                    this.$nextButton?.removeAttribute('disabled');
                }
            });

            this.$nextButton?.addEventListener('click', (e) => {
                e.preventDefault();
                this.changeStep(true, true);
                this.updateHistory();
            });

            this.$prevButton?.addEventListener('click', (e) => {
                e.preventDefault();

                if (this.allowHistoryUpdate) {
                    history.back();
                } else {
                    this.changeStep(false, false);
                }
            });
        } else {
            if (!this.autoStart) {
                this.$startButton?.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.changeStep(true, true);
                    this.updateHistory();
                });
            }

            // Handle browser history navigation.
            // We only allow navigating backwards via popstate. Forward navigation
            // is ignored to prevent jumping ahead unintentionally (e.g. when the
            // modal or component is focused and the user presses the browser's
            // forward button). This effectively makes the forward button a no-op
            // for this component, while back still works as expected.
            window.addEventListener('popstate', (e) => {
                if (!this.allowHistoryUpdate) return;
                if (e.state && typeof e.state.solutionFinder === 'number') {
                    const step = e.state.solutionFinder;
                    if (step < this.currentStep) {
                        // Allow going back only
                        this.goToStep(step);
                    } else {
                        // Ignore same-step or forward navigation
                        return;
                    }
                }
            });

            window.addEventListener('modal.opened', (e) => {
                const $modal = e.detail.modalTarget;

                if ($modal.contains(this.$root)) {
                    this.updateHistory();
                }
            });

            window.addEventListener('modal.closed', (e) => {
                const $modal = e.detail.modalTarget;
                
                if ($modal.contains(this.$root) && this.solutionIsActive) {
                    this.goToStep(this.autoStart ? 1 : 0, false);
                }
            });
        }
    }
}

export { SolutionFinder };

// Auto-initialization hook: whenever new content is loaded into the page (or modal),
// search it for solution-finder roots and initialize a controller for each of them.
window.addEventListener('content.loaded', (e) => {
    const $context = e.detail.$context;

    if ($context) {
        const $$roots = $context.querySelectorAll('[data-solutionfinder="root"]');

        $$roots.forEach($root => {
            if ($root.dataset.solutionfinderInitialized) return;

            const $dialog = $root.closest('dialog');

            if ($dialog) {
                window.addEventListener('modal.started.firstTime', (e) => {
                    const $modal = e.detail.modalTarget;
                    const $modalScroller = e.detail.modalScroller;

                    if ($modal.contains($root) && !$root.dataset.solutionfinderInitialized) {
                        const $rootAPI = new SolutionFinder($root, $modal, $modalScroller, $$roots.length === 1);
                        $rootAPI.initialize();

                        $root.dataset.solutionfinderInitialized = 'true';
                    }
                });
            } else {
                const $rootAPI = new SolutionFinder($root);
                $rootAPI.initialize();

                $root.dataset.solutionfinderInitialized = 'true';
            }
        });
    }
});